import { get, post, destroy } from './index';

/**
 *
 * @param {number} orgId
 * @param {Object} params
 * @param {number} params.limit - number of imports to return
 * @param {number} params.offset - offset for pagination
 * @returns {Promise<Object>} information about previous imports
 */
export const getImports = (orgId, params) => {
  const data = params
    ? {
      organization_id: orgId,
      limit: params.limit,
      offset: params.offset,
    }
    : { organization_id: orgId };
  const queryParamString = new URLSearchParams(data).toString();
  return get(`/api/v3/imports?${queryParamString}`);
};

/**
 * @param {number} orgId - the id of the organization
 * @param {number} importId - the id of the import
 * @returns {Promise<Object>} - the response from the server with the status of delete
 * @description - this function will delete the import with the given organization id and import id
 */

export const deleteImport = (orgId, importId) => {
  return destroy(`/api/v3/imports/${importId}?organization_id=${orgId}`);
};

/**
 * @param {number} orgId - the id of the organization
 * @param {string} type - the type of import
 * @param {File} file - the file to import
 * @returns {Promise<Object>} - the response from the server with the status of the import
 */

export const importFile = (orgId, type, file) => {
  const formData = new FormData();
  formData.append('csv', file);
  formData.append('organization_id', orgId);
  formData.append('type', type);
  return post(`/api/v3/imports?organization_id=${orgId}&type=${type}`, formData, false);
};

/**
 *
 * @param {*} interval
 * @returns {Promise<void>} delay function that returns a promise that resolves after the interval
 */

const delay = (interval) => new Promise((resolve) => setTimeout(resolve, interval));
/**
 * @param {Object} params - The parameters for the function
 * we need to pass necessary parameters to the function as an object
 * example: getImportStatus({ exportId: 1, errorMessage: errorMessage})
 * @param {number} params.exportId - Required the export id
 * @param {string} [params.errorMessage] - Optional error message to display if the export fails
 * @param {number} [params.interval=2000] - Optional interval to check if the export is ready
 * @returns {Promise<void>} status of the import
 *
 *
 */

export const getImportStatus = ({
  importId, customError, interval = 2000,
}) => {
  const runPoll = () => {
    return get(`/api/v3/imports/${importId}/status`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.statusText || customError);
        }
        // status 304 means the resource has not been modified - still waiting for the export to finish
        if (response.status === 204) {
          return delay(interval).then(() => runPoll());
        }
        if (response.status !== 200) {
          throw new Error(response.statusText || customError);
        }
        return response.json();
      });
  };
  return runPoll();
};

/**
 *
 * @param {number} importId
 * @param {Object} [params]
 * @param {number} [params.limit]  - max entries to return in this batch
 * @param {number} [params.offset] - entry offset for this batch
 * @returns {Promise<Object>} import record, optionally with a paginated entries slice
 */
export const getImport = (importId, params = {}) => {
  const queryString = Object.keys(params).length
    ? `?${new URLSearchParams(params).toString()}`
    : '';
  return get(`/api/v3/imports/${importId}${queryString}`);
};

/**
 * @param {number} importId
 * @param {number} orgId
 * @returns {Promise<Object>} url link with the previously imported file
 */
export const downloadImportFile = (importId, orgId) => {
  return get(`/api/v1/imports/${importId}/download?organization_id=${orgId}`);
};
